/**
 * Every customer-visible string on the Team page, a line's page and the
 * Agents page.
 *
 * One module so the lexicon is greppable (ADR-0044 §10, CONTEXT.md, and the
 * simplification of 2026-09-24):
 *
 *   - **Agent**, never Worker.
 *   - **Ticket**, never "work item" or "task".
 *   - A standing job is a **line**, and on the page it is a sentence with its
 *     blanks filled in and its Authority inside it. Never "job" in copy.
 *   - One Team ladder of three steps: Supervised, Autonomous, Autonomous +
 *     auto-merge. Per-line rungs are God Mode's override, never shown here.
 *     No Training, no graduate, no demote.
 *   - The six stage words: Proposed, Plan, Build, Review, QA, Done, and Any
 *     time for what is not a step.
 *   - No em-dashes, and never "Not now".
 */

import type {
  BacklogPreviewResponse,
  JobAuthority,
  JobReadiness,
  ReviewLevel,
  TeamAutonomy,
} from "@yagni/shared";
import { TICKET_OWNERS_UNASSIGNED, getConnectorCatalogEntry } from "@yagni/shared";

// ── The Team page header ───────────────────────────────────────────────────

export const TEAM_LOADING = "Reading this Team.";
export const TEAM_MISSING = "That Team is not here.";
export const TEAM_READ_FAILED = "Could not read this Team. Reload to try again.";

/** The scope line's way into the Edit sheet. */
export const TEAM_EDIT = "Edit";
export const TEAM_PAUSE = "Pause";
export const TEAM_RESUME = "Resume";
export const TEAM_LEAVE = "Leave";
export const TEAM_LEAVE_FAILED = "Could not leave this Team. Try again.";

/** Leaving a restricted Team is leaving its audience: say so, and the way back. */
export function leaveConfirm(teamName: string, ownerName: string | null): string {
  const back = ownerName
    ? `${ownerName}, its owner, can add you back.`
    : "Its owner can add you back.";
  return `Leave ${teamName}? It leaves your sidebar and you stop seeing its work. ${back}`;
}

/** "Work · 6": the Team's open tickets, one press from Work filtered to it. */
export function workButtonLabel(count: number | null): string {
  return count === null ? "Work" : `Work · ${count}`;
}

/**
 * Who can see the Team, in a customer's words, as the scope line's last fact.
 *
 * Three stored audiences read as the two the Edit sheet offers, plus the
 * personal case: `shared` is everyone, `restricted` is its members, and
 * `private` is its owner and the workspace's admins.
 */
export const AUDIENCE_WORD: Record<string, string> = {
  shared: "everyone",
  workspace: "everyone",
  standard: "everyone",
  restricted: "its members",
  personal: "its owner and admins",
  private: "its owner and admins",
  implicit: "its owner and admins",
};

export function audienceLine(visibility: string | null): string | null {
  if (!visibility) return null;
  return AUDIENCE_WORD[visibility] ?? null;
}

export function pausedLine(name: string | null, at: string | null): string {
  const who = name ? `by ${name}` : "";
  const when = at ? ` at ${new Date(at).toLocaleString()}` : "";
  return `Paused${who ? ` ${who}` : ""}${when}. Nothing on this Team starts until someone resumes it.`;
}

// ── The scope line ─────────────────────────────────────────────────────────
//
// "Works on acme/checkout · reads the Checkout project in Linear · posts to
// #checkout · asks Priya about product, Sam about architecture · seen by
// everyone · Edit". The joins are here; the facts come from the Team.

export const SCOPE_WORKS_ON = "Works on";
export const SCOPE_NO_REPOS = "no repository yet";
export const SCOPE_POSTS_TO = "posts to";
export const SCOPE_POSTS_NOWHERE = "posts nowhere";
export const SCOPE_POSTS_TO_PR = "posts to the pull request";
export const SCOPE_ASKS = "asks";
export const SCOPE_SEEN_BY = "seen by";
export const SCOPE_SOURCE_PULL_REQUESTS = "every pull request opened there";
export const SCOPE_READS_PULL_REQUESTS = `reviews ${SCOPE_SOURCE_PULL_REQUESTS}`;
export const SCOPE_PROPOSES_FROM_CODE = "proposes from the code";
export const SCOPE_WORKS_FROM = "works from";

/** The three kinds a Team names answerers for, in the order the line says them. */
export const QUESTION_KINDS: ReadonlyArray<{ key: string; about: string }> = [
  { key: "product", about: "product" },
  { key: "architecture", about: "architecture" },
  { key: "domain", about: "the domain" },
];

/** "about product": the question kind as the scope line and the sheet say it. */
export function questionAbout(kind: string): string {
  return QUESTION_KINDS.find((entry) => entry.key === kind)?.about ?? kind;
}

// ── The line ───────────────────────────────────────────────────────────────

export const AUTHORITY_AUTONOMOUS = "autonomous";
/** The merge line's Authority: the permanent floor, said as a fact. */
export const AUTHORITY_MERGE = "always a person";

/** "supervised by Priya", or the honest shape when nobody is named. */
export function supervisedBy(name: string | null): string {
  return name ? `supervised by ${name}` : "supervised, nobody named";
}

export function authorityWord(
  authority: JobAuthority | null,
  supervisorName: string | null,
): string | null {
  if (authority === null) return null;
  return authority === "autonomous" ? AUTHORITY_AUTONOMOUS : supervisedBy(supervisorName);
}

/**
 * The status word. On says nothing on the Team page; only a line that is off,
 * paused or waiting on setup carries a word at its right edge.
 */
export const READINESS_WORD: Record<JobReadiness, string> = {
  on: "on",
  off: "off",
  paused: "paused",
  needs_setup: "needs setup",
};

export function readinessLine(readiness: JobReadiness, phrase: string | null): string {
  if (readiness !== "needs_setup") return READINESS_WORD[readiness];
  return phrase ? `needs setup: ${phrase}` : READINESS_WORD.needs_setup;
}

/** Kept for the create page's pipeline, which labels a line's switch with it. */
export const LINE_SWITCH_LABEL = "This line";
/** Kept for the create page's pipeline, which heads its any-time lines with it. */
export const QUESTIONS_LABEL = "Questions";

/**
 * A repository this Team attaches and another Team reviews.
 *
 * One Team reviews a repository. Where that Team is not this one, the review
 * line's page says so in plain words, with the one act that changes the
 * answer (plan 2026-09-16, decision 14).
 */
export function reviewedByLine(teamName: string): string {
  return `Reviewed by ${teamName}`;
}

export const REVIEW_HERE_INSTEAD = "Review here instead";

export const LINES_EMPTY = "No line on this Team yet.";

// ── The three cards under the lines ────────────────────────────────────────

export const CARD_EDIT = "Edit";
export const CARD_ADD = "Add";

export function playbookCardLabel(count: number | null): string {
  if (count === null || count === 0) return "Playbook";
  return `Playbook · ${count} ${count === 1 ? "rule" : "rules"}`;
}

export function playbookCardLine(rules: readonly string[] | null): string {
  if (rules === null) return "The Playbook could not be read. Reload to try again.";
  if (rules.length === 0)
    return "No rules yet. Every line but Harper's reports reads what you add here.";
  return rules
    .slice(0, 3)
    .map((rule) => `"${rule.replace(/\.+$/, "")}."`)
    .join(" ");
}

export const PEOPLE_LABEL = "People";

/** "Priya, owner. Sam answers architecture questions." */
export function peopleCardLine(
  owner: string | null,
  answerers: ReadonlyArray<{ name: string; about: string }>,
): string {
  const parts: string[] = [owner ? `${owner}, owner.` : "Nobody owns this Team yet."];
  for (const answerer of answerers) {
    parts.push(`${answerer.name} answers ${answerer.about} questions.`);
  }
  return parts.join(" ");
}

// ── The Edit sheet ─────────────────────────────────────────────────────────

export const EDIT_LEAD = "Everything that is not a line.";
export const EDIT_NAME = "Name";
export const EDIT_OWNS = "Owns";
export const EDIT_OWNS_PLACEHOLDER = "What this Team owns, in a sentence or two.";
/** Under the budget, for a viewer who cannot manage the Team. */
export const LIMITS_READ_ONLY_NOTE = "Only the Team's owner or a workspace admin can change these.";
export const EDIT_WORKS_ON = "Works on";
export function editRepoSearchPlaceholder(count: number): string {
  return count > 0 ? `Search ${count} repositories` : "No repository to add yet";
}
export const EDIT_REPO_REMOVE = (repo: string) => `Remove ${repo}`;
export const EDIT_READS = "Reads";
export const EDIT_WHOSE = "Whose tickets";
export const EDIT_WHOSE_ANYONE = "Anyone's";
export const EDIT_WHOSE_UNASSIGNED = "Unassigned only";
export const EDIT_WHOSE_CHOSEN = "The people chosen before";
export const EDIT_WHOSE_PERSON = (name: string) => `Assigned to ${name}`;
export const EDIT_POSTS_TO = "Posts to";
export const EDIT_POSTS_NOWHERE = "Nowhere";
export const EDIT_ASKS = "Asks";
export const EDIT_ASKS_ABOUT = (name: string) => `${name} answers about`;
export const EDIT_ASKS_ADD = "Add a person";
export const EDIT_ASKS_REMOVE = (name: string) => `Stop asking ${name}`;
export const EDIT_SEEN_BY = "Seen by";
export const EDIT_SEEN_EVERYONE = "everyone in the workspace";
export const EDIT_SEEN_MEMBERS = "members only";
export const EDIT_PAUSE = "Pause team";
export const EDIT_RESUME = "Resume team";
export const EDIT_ARCHIVE = "Archive";
export const EDIT_ARCHIVE_CONFIRM =
  "Archive this Team? Its lines stop and it leaves the sidebar. Its record stays.";
export const EDIT_CANCEL = "Cancel";
export const EDIT_SAVE = "Save";
export const EDIT_SAVING = "Saving";
export const EDIT_UNREACHABLE = "Could not reach the server. Try again.";

// ── The Playbook sheet ─────────────────────────────────────────────────────

export const PLAYBOOK_TITLE = "Playbook";
export const PLAYBOOK_LEAD =
  "Standing instructions every line on this Team reads, except Harper's reports, which follow their template and instructions. Rules the Team proposes for itself are already live: adopt one to keep it, or dismiss it to stop it applying.";
export const PLAYBOOK_EMPTY = "No standing rules yet. Add the first one below.";
export const PLAYBOOK_ADD = "Add rule";
export const PLAYBOOK_ADD_PLACEHOLDER = "Add a standing rule, e.g. Migrations ship separately.";
export const PLAYBOOK_ADOPT = "Adopt";
export const PLAYBOOK_DISMISS = "Dismiss";
export const PLAYBOOK_RETIRE = "Retire";
export const PLAYBOOK_CLOSE = "Close";
export const PLAYBOOK_PROPOSED = "proposed";
export const PLAYBOOK_PROPOSED_HINT =
  "Proposed by the Team from its own work. It is already applying; adopt to keep it or dismiss to stop it.";
export const PLAYBOOK_WORKSPACE = "workspace";
export const PLAYBOOK_WORKSPACE_HINT =
  "Set for the whole workspace. Every Team follows it; this Team can add its own rule that overrides it.";
export const PLAYBOOK_OVERRIDES = "Overrides a workspace rule";

export const MONTH_LABEL = "This month";
export const MONTH_USAGE = "Usage";

/** "$41.20 spent by this Team's Agents." Null when spend is not the reader's to see. */
export function monthCardLine(spendMillicents: number | null): string {
  if (spendMillicents === null) return "What this Team's Agents spent is on Usage.";
  return `${agentSpendDollars(spendMillicents)} spent by this Team's Agents.`;
}

// ── A line's page ──────────────────────────────────────────────────────────

export const LINE_MISSING = "That line is not on this Team.";
export const LINE_BACK_FALLBACK = "The Team";
export const LINE_MORE = "More";
export const LINE_TURN_OFF = "Turn off";
export const LINE_TURN_ON = "Turn on";
export const LINE_REMOVE = "Remove";
export const LINE_REMOVE_CONFIRM = "Remove this line? Its record stays on the Team.";
export const LINE_AUTHORITY_LABEL = "Authority";
/** Under a blank that bounds an Autonomous line, while the line is Supervised. */
export const LINE_AUTONOMOUS_ONLY_NOTE = "Only used when this line is Autonomous.";
/**
 * What a review line's clean review does on GitHub. The line's Authority is
 * the whole answer (ADR-0047, amended 2026-10-13).
 */
export const LINE_APPROVE_GITHUB_LABEL = "On GitHub";
export const LINE_APPROVE_GITHUB_SUPERVISED =
  "Posts the review and says it would approve. Pick Autonomous for the Team, at the top of these settings, to approve the pull request.";
/**
 * On a Team's own pull request the approval is sent by the separate YAGNI
 * Review App (ADR-0047, amended 2026-10-14); without it GitHub refuses an
 * author's approval, so the review says approval is advised.
 */
export const LINE_APPROVE_GITHUB_AUTONOMOUS =
  "Posts the review and approves the pull request. On the Team's own pull requests the approval is sent by the YAGNI Review App when it is installed on the repository; without it, the review says approval is advised.";
export const LINE_APPROVE_GITHUB_HINT =
  "An approval counts toward branch protection's required reviews.";
/** A review line's level: how hard Proctor reviews (`codeReview/reviewLevel.ts`). */
export const LINE_REVIEW_LEVEL_LABEL = "Review level";
export const REVIEW_LEVEL_COPY: Record<ReviewLevel, { label: string; note: string }> = {
  light: {
    label: "Light",
    note: "Posts only risks: things likely to break or corrupt something. It approves when no risk is left. Smaller notes stay on the run in YAGNI.",
  },
  balanced: {
    label: "Balanced",
    note: "Posts every finding it can stand behind, and approves when nothing blocking is left. This is how Proctor reviews today.",
  },
  tough: {
    label: "Tough",
    note: "Posts every finding. It approves only when nothing above a nit is left, and earlier comments above a nit are fixed or answered.",
  },
};
/** Under a number blank holding something outside its range. */
export function slotRangeError(min: number, max: number): string {
  return `Choose a whole number from ${min} to ${max}.`;
}
export const LINE_SUPERVISED_BY = "supervised by";
export const LINE_SUPERVISOR_LABEL = "Supervisor";
export const LINE_NOTE_LABEL = "With these instructions";
export const LINE_NOTE_PLACEHOLDER = "Anything this line should always do or never do.";

/** Under Groups by on a Harper line that posts when a production deploy lands. */
export const LINE_GROUPS_BY_DEPLOY_NOTE =
  "Posts when a production deploy lands, so it groups by deploy: only what shipped.";

/** Under the instructions: where they sit among what the line reads. */
export function lineNoteHint(rules: number | null): string {
  if (rules === null || rules === 0) return "Read on every run, above the Team's Playbook.";
  return `Read on every run, above the Playbook's ${rules} ${rules === 1 ? "rule" : "rules"}.`;
}

/** The fixed footer's one consequence line. */
export function lineConsequence(kind: string): string {
  return kind.startsWith("reviewer.")
    ? "Changes apply to the next review, not the ones in flight."
    : "Changes apply to the next run, not the ones in flight.";
}

/** The header's run button, in the line's own verb. */
export function runNowLabel(kind: string): string {
  if (kind.startsWith("reviewer.")) return "Review now";
  if (kind === "pm.propose") return "Propose now";
  return "Run now";
}

/**
 * The rhythms a person actually asks for, over the editor rather than inside
 * it. Clicking one writes it; editing the times below lands on "Custom", which
 * is a label and not a button.
 */
export const WHEN_PRESETS_LABEL = "Common rhythms";
export const WHEN_PRESET_WEEKDAYS = "Weekdays at 09:00";
export const WHEN_PRESET_EVERY_DAY = "Every day at 09:00";
export const WHEN_PRESET_TWICE = "Weekdays at 09:00 and 17:00";
export const WHEN_PRESET_CUSTOM = "Custom";

/**
 * How often a queue or spine line comes round. A number box asked a person to
 * invent minutes; these are the answers the scheduler is built for.
 */
export const INTERVAL_CHOICES: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 30, label: "every 30 minutes" },
  { minutes: 60, label: "every hour" },
  { minutes: 120, label: "every 2 hours" },
  { minutes: 240, label: "every 4 hours" },
  { minutes: 1440, label: "daily" },
];
export const CARD_SAVE = "Save";
export const CARD_SAVING = "Saving";
export const CARD_CANCEL = "Cancel";
export const CARD_RUNNING = "Starting";
export const CARD_RUN_STARTED = "Started. It will show up in the runs below.";
export const CARD_RETRY = "Retry";
export const CARD_RETRYING = "Retrying";
export const CARD_RETRY_DONE = "Retried. The runs below show how it went.";
export const CARD_RUNS_LABEL = "Recent runs";
export const CARD_RUNS_EMPTY = "It has not run yet.";

/**
 * The one action beside a stopped run, from its failure code's copy
 * (`jobFailureCopy(code).action`). Capacity is added in Settings; a delivery
 * error is fixed on this page's channel blank, or in Connections when the
 * connection itself needs redoing. Never a meter's name.
 */
export const LINE_ADD_CAPACITY = "Add capacity";
export const LINE_ADD_CAPACITY_HREF = "/settings/spending";
export const LINE_CHANGE_CHANNEL = "Change the channel";
export const LINE_OPEN_CONNECTIONS = "Open Connections";
export const LINE_CONNECTIONS_HREF = "/services";
/** A line with no engine retry starts a fresh run instead. */
export const LINE_RUN_AGAIN = "Run again";
/**
 * Beside a firing an Autonomous propose line started on its own between its
 * trigger times, because it still had tickets left to take today.
 */
export const LINE_RUN_REFIRE = "Again, toward today's limit";
/** The run cause's `sourceRef` that marks such a firing (`desks/proposalLane/refire.ts`). */
export const RUN_CAUSE_INTAKE_REFIRE = "intake_refire";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** "Fri 9/26", in the reader's own clock. */
export function shortDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return `${WEEKDAYS[at.getDay()]} ${at.getMonth() + 1}/${at.getDate()}`;
}

/** A missed slot's row: "Missed Fri 9/26 · out of capacity · caught up Mon 9/28". */
export function missedSlotLine(slotAt: string, phrase: string, caughtUpAt: string | null): string {
  return [
    `Missed ${shortDay(slotAt)}`,
    phrase,
    caughtUpAt ? `caught up ${shortDay(caughtUpAt)}` : "not caught up yet",
  ].join(" · ");
}

/**
 * What a Retry came to, read from the retried run's status. A run that stopped
 * again says why in the code's own sentence; a hold is not a failure, so it
 * says what happens next instead.
 */
export function retryOutcomeLine(
  status: string,
  copy: { sentence: string; nextStep: string },
): string {
  if (status === "held") return `${copy.sentence} ${copy.nextStep}`;
  if (status === "failed" || status === "delivery_failed" || status === "needs_attention") {
    return `failed again: ${copy.sentence}`;
  }
  return CARD_RETRY_DONE;
}

/** The Team page's word for an on line that stopped posting: "held: out of capacity". */
export function continuityWord(word: string, phrase: string | null): string {
  return phrase ? `${word}: ${phrase}` : word;
}

/**
 * The permanent floor, said where a person reaches for the rung it refuses.
 * Merge, deploy and data-migration capabilities cap at supervised.
 */
export const CEILING_NOTE = "This one always waits for a person. That does not change.";

/**
 * What Wright's Build rung means, said under the rung picker. Every pull
 * request opens ready for review (2026-10-19); only a build that did not pass
 * every check opens as a draft, on either rung. The rung decides how that
 * draft leaves draft (ADR-0049 amendment 2026-10-14). Merge is a separate
 * line and always a person.
 */
export const BUILD_RUNG_NOTE = {
  supervised:
    "Pull requests open ready for review. A build that did not pass every check opens as a draft and stays a draft until a fix round passes or a person takes it out of draft.",
  autonomous:
    "Pull requests open ready for review. A build that did not pass every check opens as a draft and leaves draft when review lets it go, with what failed written on the pull request.",
} as const;

/** The record's counts, as the line's page and the Agents page print them. */
export interface RecordCounts {
  total: number;
  acceptedAsProposed: number;
  edited: number;
  sentBack: number;
  autoShipped: number;
  reversed: number;
}

/**
 * "22 accepted as proposed", "2 edited", "1 sent back", "3 acted alone",
 * "1 reversed": the counts the record holds, the first always and the rest
 * when they happened. A line with no record says so.
 */
export function recordCountPhrases(record: RecordCounts): string[] {
  if (record.total === 0) return ["no record yet"];
  const out = [`${record.acceptedAsProposed} accepted as proposed`];
  if (record.edited > 0) out.push(`${record.edited} edited`);
  if (record.sentBack > 0) out.push(`${record.sentBack} sent back`);
  if (record.autoShipped > 0) out.push(`${record.autoShipped} acted alone`);
  if (record.reversed > 0) out.push(`${record.reversed} reversed`);
  return out;
}

export function promotedLine(at: string, name: string | null): string {
  const who = name ? ` by ${name}` : "";
  return `Made autonomous${who} on ${new Date(at).toLocaleDateString()}`;
}

// ── Asking an event line to run on one pull request ────────────────────────

export const ASK_PR_LABEL = "Pull request";
export const ASK_PR_PLACEHOLDER_NUMBER = "1537";
export const ASK_PR_PLACEHOLDER_URL = "1537, or paste the link";
export const ASK_REPO_LABEL = "In";
export const ASK_CANCEL = "Cancel";
export const ASK_SENDING = "Asking";
export const ASK_SENT = "Asked. It will show up in Work.";

export function askUnreadable(accepts: "number" | "number-or-url"): string {
  return accepts === "number"
    ? "Give the pull request number, like 1537."
    : "Give the pull request number, like 1537, or paste its link.";
}

/**
 * The line covers some repositories and the ask named another.
 *
 * Checked against the LINE rather than the Team because the line is what would
 * do the work: asking a shared-svc line about a pricing pull request is a
 * request nothing would ever answer, and the sentence says which line to use.
 */
export function askNotThisLinesRepo(named: string, covered: readonly string[]): string {
  return `This line covers ${covered.join(", ")}, not ${named}. Ask the line that covers it.`;
}

// ── Add ────────────────────────────────────────────────────────────────────

export const ADD_LINE = "Add a line";
export const ADD_TITLE = "What should this Team's Agents do?";
export const ADD_AGENT = "Add an Agent";
export const ADD_AGENT_HINT = "Its capabilities show up here once it is on the Team";
export const ADD_NOTHING_LEFT =
  "Every capability these Agents have is already set up here. Add an Agent for more.";
export const ADD_INSTANCE_LABEL = "Call this line";
export const ADD_INSTANCE_HINT = "A short name, so two lines of the same thing read apart";

/**
 * A Harper line's instructions, asked for when it is added. On a Custom line
 * they are the job; on a template line they only add to it.
 */
export const ADD_INSTRUCTIONS_LABEL = "What should it report on?";
export const ADD_INSTRUCTIONS_HINT =
  "In your own words: who reads it, what matters to them, what to leave out.";
export const ADD_INSTRUCTIONS_OPTIONAL_LABEL = "Anything to add?";
export const ADD_INSTRUCTIONS_OPTIONAL_HINT =
  "Optional. In your own words: what to stress, what to leave out, a format you want.";
export const ADD_CANCEL = "Cancel";
export const ADD_CONFIRM = "Add it";

// ── Refusals, keyed by the server's code ───────────────────────────────────

/**
 * The run-now refusals. Read the `code`, never the `error`.
 *
 * A switched-off line is not one of them: off stops the clock and source
 * events, never a person (the one rule for off), so Run now on an off line
 * runs. What refuses a person is a paused or archived Team, or an Agent who
 * is not on the Team. The route sends the rule's codes (`team_paused`,
 * `team_archived`, `not_engaged`) and, from the start itself, the short ones
 * (`paused`, `archived`); both read the same.
 */
export const RUN_NOW_REFUSAL: Record<string, string> = {
  deduplicated: "It is already running. Give it a moment.",
  paused: "This Team is paused, so nothing starts until someone resumes it.",
  team_paused: "This Team is paused, so nothing starts until someone resumes it.",
  archived: "This Team is archived, so nothing starts on it.",
  team_archived: "This Team is archived, so nothing starts on it.",
  not_engaged: "This Agent is not on the Team right now. Add them back to run this.",
  run_now_refused: "This one starts when something happens, so there is nothing to run on demand.",
  event_rhythm: "This one starts when something happens, so there is nothing to run on demand.",
};

/**
 * The retry refusals. Read the `code`, never the `error` — the same
 * discipline as the run-now refusals above.
 */
export const CARD_RETRY_REFUSAL: Record<string, string> = {
  not_a_harper_job: "This line is not one of Harper's jobs, so it has no run to retry here.",
  nothing_to_retry: "This line has no failed run to retry.",
  not_retryable: "This run already settled cleanly.",
  no_rhythm: "This Team has no record to retry the run under.",
  retry_claim_lost: "It is already being retried. Give it a moment.",
  team_paused: "This Team is paused, so nothing starts until someone resumes it.",
  forbidden: "You do not have permission to change this Team.",
};

export const WRITE_REFUSAL: Record<string, string> = {
  partition_overlap:
    "Another line already covers some of this. Narrow one of them so they do not both answer.",
  authority_above_ceiling: CEILING_NOTE,
  supervisor_not_a_member: "That person is not on this Team. Add them first.",
  supervisor_required: "A supervised line needs a person's name on it.",
  not_multi_instance: "This Agent does this once here, and it is already set up.",
  instance_exists: "There is already a line called that.",
  instance_required: "Give the line a short name.",
  default_instance_protected:
    "This is the Agent's own line here. Switch it off rather than removing it.",
  desk_archived: "This Team is archived. Its lines are part of the record now.",
  answerer_not_a_member: "An answerer has to be on the Team. Add them first.",
  owner_cannot_leave: "You own this Team. Hand it to someone else first, in Edit.",
  forbidden: "You do not have permission to change this Team.",
  run_limits_invalid:
    "One of the run limits is outside what a Team can hold. The note under that field says why.",
};

export function refusalLine(
  code: string,
  fallback: string,
  table: Record<string, string> = WRITE_REFUSAL,
): string {
  return table[code] ?? fallback;
}

// ── The backlog blank ──────────────────────────────────────────────────────
//
// A Team attaches a tracker project, epic, view, or Jira filter or query as
// its backlog, and the attachment's own label is whatever the tracker calls it
// ("YAGNI Code"), which on a Team named YAGNI Code reads as the Team, not the
// source. So the blank prints the source beside the label everywhere the
// label appears.

/** A tracker's name as a person says it. Unknown sources print as they came. */
export const BACKLOG_SOURCE_WORD: Record<string, string> = {
  linear: "Linear",
  jira: "Jira",
  github: "GitHub",
};

/** The longest a surface prints of a filter or query before it is cut. */
export const FILTER_MAX = 44;

/**
 * A pasted query as a surface prints it: whole when it fits, else cut back to
 * the last whole token with a trailing ellipsis, never mid-word. Nothing is
 * paraphrased, so a cut always lands between the query's own words (YAG-829).
 */
export function truncatedQueryWords(text: string, max: number = FILTER_MAX): string {
  if (text.length <= max) return text;
  const room = text.slice(0, max + 1);
  const cut = room.lastIndexOf(" ");
  const head = (cut > 0 ? room.slice(0, cut) : room.slice(0, max)).trimEnd();
  return `${head}…`;
}

/**
 * What a held backlog reads as once the field folds: one line naming what the
 * Team will read, in the same words the answer under the box uses.
 *
 * A pasted saved-filter link names what the tracker calls it ("Jira filter
 * 10042") with the JQL it runs as, because a URL cut at 44 characters names
 * nothing a person recognises and two filters on one host read alike. A typed
 * JQL, a key list and a Linear view name the text as written, cut on a token
 * (YAG-829). Never an em-dash.
 */
export function heldBacklogLine(
  answer: BacklogPreviewResponse,
  source: string | undefined,
): string {
  const tracker = BACKLOG_SOURCE_WORD[source ?? "jira"] ?? source ?? "Jira";
  if (answer.resolved) {
    return `${tracker} ${answer.resolved.name}: ${truncatedQueryWords(answer.resolved.jql, FILTER_MAX)}`;
  }
  if (answer.kind === "keys") {
    return `${tracker} keys: ${truncatedQueryWords(answer.filter, FILTER_MAX)}`;
  }
  // A Linear view can only have come from Linear, whatever the mount's tracker says.
  if (answer.kind === "linear_view") {
    const linear = BACKLOG_SOURCE_WORD.linear;
    return `${linear} view: ${truncatedQueryWords(answer.filter, FILTER_MAX)}`;
  }
  return `${tracker} query: ${truncatedQueryWords(answer.filter, FILTER_MAX)}`;
}

/**
 * The attachment's kind, as a person says it. A Jira saved filter or query
 * attaches as a view (a selection the tracker owns) and its ref says which.
 */
export function backlogKindWord(kind: string, source: string, ref: string): string | null {
  if (kind === "tracker_project") return "project";
  if (kind === "tracker_epic") return "epic";
  if (kind !== "tracker_view") return null;
  if (source === "jira" && ref.startsWith("filter:")) return "filter";
  if (source === "jira" && ref.startsWith("jql:")) return "query";
  return "view";
}

export interface BacklogView {
  /** The reference the line stores: a project id, an epic key, a view id. */
  ref: string;
  label: string;
  /** The connector the reference lives in, e.g. `linear`, `jira`. */
  source: string;
  kind: string;
}

function backlogSourceWord(view: BacklogView): string {
  return BACKLOG_SOURCE_WORD[view.source] ?? view.source;
}

/**
 * "YAGNI Code (Linear)": the backlog as the sentence names it. A label that
 * already ends in its key keeps one pair of brackets: "Mild (MILD, Jira)".
 */
export function backlogName(view: BacklogView): string {
  const keyed = /^(.*\S)\s*\(([^()]+)\)$/.exec(view.label);
  if (keyed) return `${keyed[1]} (${keyed[2]}, ${backlogSourceWord(view)})`;
  return `${view.label} (${backlogSourceWord(view)})`;
}

const JIRA_ISSUE_KEY = /^[A-Za-z][A-Za-z0-9_]*-\d+$/;

/** A Jira epic's label leads with its key, "MILD-12 · Checkout rework", so two alike read apart. */
function keyedLabel(view: BacklogView): string {
  if (
    view.source === "jira" &&
    view.kind === "tracker_epic" &&
    JIRA_ISSUE_KEY.test(view.ref) &&
    !view.label.includes(view.ref)
  ) {
    return `${view.ref} · ${view.label}`;
  }
  return view.label;
}

/** Every attached backlog in one phrase, or null when none is attached. */
export function attachedBacklogsPhrase(views: readonly BacklogView[]): string | null {
  if (views.length === 0) return null;
  const names = views.map(backlogName);
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The picker's first option: the blank left empty, said in the Team's terms. */
export function backlogEmptyOption(views: readonly BacklogView[]): string {
  const phrase = attachedBacklogsPhrase(views);
  if (!phrase) return "the attached backlog · none attached yet";
  return views.length === 1
    ? `the attached backlog · ${phrase}`
    : `every attached backlog · ${phrase}`;
}

/** Under the picker. The line reads the Team's attachments whatever it says here. */
export function backlogNote(agentName: string | undefined): string {
  const who = agentName?.trim() || "This line";
  return `${who} reads every backlog attached to this Team. Naming one here only tells two propose lines apart; choosing one the Team does not have yet attaches it when you save.`;
}

/** Save refused at the attach step: the line is untouched, and the reader hears both. */
export function backlogAttachRefused(view: BacklogView, reason: string): string {
  return `Could not attach ${backlogName(view)} to this Team: ${reason.replace(/\.$/, "")}. The line was not changed.`;
}

/**
 * A backlog the Team cannot read until an admin opens its projects. The same
 * words before Create (`jiraQueryAccessLines`) and after it
 * (`BacklogAccessNotice`), so a person reads one message twice.
 */
export function backlogNeedsAdminLine(names: readonly string[]): string {
  const pronoun = names.length > 1 ? "them" : "it";
  return `The Team can read ${joinWords(names) || "its projects"} once a workspace admin opens ${pronoun} under Connections.`;
}

/**
 * The backlog's name on the Create page's card. A Jira query's own label is
 * short ("DCP query"); an older one is the raw JQL, which never shows as a name.
 */
export function backlogCardTitle(attachment: {
  source: string;
  ref: string;
  label?: string | null;
}): string {
  const label = attachment.label?.trim() ?? "";
  if (attachment.source === "jira" && attachment.ref.startsWith("jql:")) {
    return !label ||
      label.toLowerCase().startsWith("jql:") ||
      label === attachment.ref.slice(4).trim()
      ? "Jira query"
      : label;
  }
  return label || attachment.ref;
}

/**
 * Who a Team's backlog is opened to: everyone in the workspace (the default
 * Team), or this Team alone (a restricted or personal Team, which an attach
 * reaches by a grant, never by opening the project to the workspace).
 */
export type BacklogAudience = "workspace" | "team";

/**
 * Who makes a Jira query's projects readable by the Team, as the create or
 * attach does it (backlogAccess): an admin's opens them to the workspace, or
 * for a restricted or personal Team grants them to this Team only, up to
 * five; a member asks an admin.
 */
export function jiraQueryAccessLines(
  projects: readonly string[],
  admin: boolean,
  audience: BacklogAudience = "workspace",
): string[] {
  const names = joinWords(projects) || "its projects";
  const pronoun = projects.length === 1 ? "it" : "them";
  if (!admin) {
    return audience === "team"
      ? // backlogAccess lets the member who connected a project grant it to a
        // restricted Team, so the line holds for the owner and for everyone else.
        [
          `The Team can read ${names} once ${pronoun === "it" ? "it is" : "they are"} granted to this Team. Your attach grants ${pronoun} if you connected Jira; otherwise a workspace admin grants ${pronoun} under Connections.`,
        ]
      : [backlogNeedsAdminLine(projects)];
  }
  const first =
    audience === "team"
      ? `Attaching grants ${names} to this Team only, so the Team can read ${pronoun}. The rest of the workspace still cannot.`
      : `Creating opens ${names} to everyone in this workspace so the Team can read ${pronoun}.`;
  return [
    first,
    ...(projects.length > 5
      ? [
          audience === "team"
            ? "Only the first five projects are granted. Ask a workspace admin to grant the rest under Connections."
            : "Only the first five projects are opened. Ask a workspace admin to open the rest under Connections.",
        ]
      : []),
  ];
}

/**
 * The sentence's `nameOf` for a Team with these backlogs: a stored reference
 * prints as the backlog's name rather than its id, and an empty blank prints
 * the attached backlogs rather than the words "the attached backlog".
 */
export function backlogNameOf(
  views: readonly BacklogView[],
): (slot: string, value: unknown) => string | null {
  return (slot, value) => {
    if (slot !== "backlogView") return null;
    if (typeof value === "string" && value.trim()) {
      const view = views.find((entry) => entry.ref === value.trim());
      return view ? backlogName(view) : null;
    }
    return attachedBacklogsPhrase(views);
  };
}

/**
 * The sentence's `nameOf` for a line on this Team: the backlog as
 * `backlogNameOf` names it, and a filled `{channel}` blank by the channel's
 * label rather than its Slack id. An empty channel blank keeps its own words
 * ("this Team's delivery target"); a channel the Team no longer lists prints
 * as stored.
 */
export function lineNameOf(
  views: readonly BacklogView[],
  channels: readonly { channelId: string; label: string }[],
): (slot: string, value: unknown) => string | null {
  const backlog = backlogNameOf(views);
  return (slot, value) => {
    if (slot === "channel") {
      if (typeof value !== "string" || !value.trim()) return null;
      return channels.find((channel) => channel.channelId === value.trim())?.label ?? null;
    }
    return backlog(slot, value);
  };
}

// ── Whose tickets ──────────────────────────────────────────────────────────
//
// Whose tracker tickets a Team works from is the Team's (migration 0263), not
// Bailey's propose line's: switching the line off used to hide the choice. It
// is set when the Team is created, changed in the Edit sheet, and named in the
// Team page's scope line.

/** A person the source phrase may name. */
export interface TicketOwnerPerson {
  userId: string;
  displayName?: string | null;
  email?: string | null;
}

export interface TicketSourceInput {
  /** The Team's ticket owners: member ids and `"unassigned"`. Null is anyone's. */
  ticketsAssignedTo: readonly string[] | null;
  /** The person reading, so their own name reads "my". */
  viewerUserId: string | null;
  members: readonly TicketOwnerPerson[];
  /** The workspace's connected trackers. One names itself; several read "tracker". */
  trackers: readonly string[];
  /** The Team's attached backlogs. */
  backlogs: readonly BacklogView[];
  /** Whether any propose line on the Team is switched on. */
  proposeOn: boolean;
  /**
   * Every line on the Team is a review line (the one-click Proctor Team).
   * Tickets never come into such a Team, so its work is the pull requests.
   */
  reviewOnly?: boolean;
}

/** Several people, a former member, or people and unassigned together. */
export const TICKET_SOURCE_CHOSEN = "the tickets you chose";
/** Nobody named, no backlog, a propose line on. */
export const TICKET_SOURCE_PROPOSED = "what Bailey finds in the repo";
/** Nobody named, no backlog, nothing proposing. */
export const TICKET_SOURCE_ADDED = "what you add";

/** "a", "a and b", "a, b and c". */
function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/**
 * "Jira tickets" when exactly one tracker is connected, "tracker tickets" when
 * several, plain "tickets" when none.
 */
function trackerTicketsWord(trackers: TicketSourceInput["trackers"]): string {
  if (trackers.length === 0) return "tickets";
  if (trackers.length > 1) return "tracker tickets";
  const tracker = trackers[0]!;
  return `${BACKLOG_SOURCE_WORD[tracker] ?? tracker} tickets`;
}

/**
 * Where this Team's work comes from, as the header's "from ..." clause says it.
 *
 * Pure so every case is pinned by a test rather than by a render. Plain words,
 * and only the forms a person reads at a glance; anything longer points at the
 * setting (the phrase is a link to it) instead of spelling it out:
 *
 *   - only the reader named: "my Jira tickets" (the tracker's name when
 *     exactly one is connected, "tracker tickets" when several, plain
 *     "tickets" when none; "my tickets in OON (Jira)" with a backlog);
 *   - one other person named: "Priya's Jira tickets";
 *   - only "unassigned" chosen: "unassigned Jira tickets";
 *   - anything more (several people, a former member, people and unassigned
 *     together): "the tickets you chose";
 *   - nobody named, a backlog attached: the backlog, as it always read;
 *   - nobody named, no backlog: "what Bailey finds in the repo" while a
 *     propose line is on, and "what you add" while nothing proposes;
 *   - a review-only Team: "every pull request opened there", whoever's
 *     tickets are named, because no ticket ever comes into it.
 */
export function ticketSourcePhrase(input: TicketSourceInput): string {
  if (input.reviewOnly) return SCOPE_SOURCE_PULL_REQUESTS;
  const owners = (input.ticketsAssignedTo ?? []).filter((entry) => entry !== "");
  const unassigned = owners.includes(TICKET_OWNERS_UNASSIGNED);
  const people = owners.filter((entry) => entry !== TICKET_OWNERS_UNASSIGNED);
  const backlog = input.backlogs.length > 0 ? attachedBacklogsPhrase(input.backlogs) : null;

  if (people.length === 0 && !unassigned) {
    if (input.backlogs.length > 0) return joinWords(input.backlogs.map((view) => view.label));
    return input.proposeOn ? TICKET_SOURCE_PROPOSED : TICKET_SOURCE_ADDED;
  }

  // With a backlog attached, the backlog names the tracker already.
  const noun = backlog ? "tickets" : trackerTicketsWord(input.trackers);
  const where = backlog ? ` in ${backlog}` : "";

  if (people.length === 0) return `unassigned ${noun}${where}`;
  if (people.length > 1 || unassigned) return TICKET_SOURCE_CHOSEN;

  const only = people[0]!;
  if (input.viewerUserId !== null && only === input.viewerUserId) return `my ${noun}${where}`;
  const member = input.members.find((entry) => entry.userId === only);
  const name = member?.displayName?.trim() || member?.email || null;
  // Somebody who has since left is not a name the page can print.
  if (!name) return TICKET_SOURCE_CHOSEN;
  return `${possessive(name)} ${noun}${where}`;
}

/** "the Checkout project in Linear": one backlog, as the scope line reads it. */
export function backlogReadPhrase(view: BacklogView): string {
  const kind = backlogKindWord(view.kind, view.source, view.ref);
  return `the ${view.label}${kind ? ` ${kind}` : ""} in ${backlogSourceWord(view)}`;
}

/**
 * Where the work comes from, as the scope line's second clause.
 *
 *   - a review-only Team: "reviews every pull request opened there";
 *   - whose tickets named: "works from my Jira tickets" (the phrase above);
 *   - a backlog attached: "reads the Checkout project in Linear";
 *   - neither, a propose line on: "proposes from the code";
 *   - neither, nothing proposing: "works from what you add".
 */
export function readsClause(input: TicketSourceInput): string {
  if (input.reviewOnly) return SCOPE_READS_PULL_REQUESTS;
  const owners = (input.ticketsAssignedTo ?? []).filter((entry) => entry !== "");
  if (owners.length > 0) return `${SCOPE_WORKS_FROM} ${ticketSourcePhrase(input)}`;
  if (input.backlogs.length > 0) {
    return `reads ${joinWords(input.backlogs.map(backlogReadPhrase))}`;
  }
  return input.proposeOn ? SCOPE_PROPOSES_FROM_CODE : `${SCOPE_WORKS_FROM} ${TICKET_SOURCE_ADDED}`;
}

/** Millicents to dollars. 100,000 millicents is one dollar. */
export function agentSpendDollars(millicents: number): string {
  return `$${(millicents / 100_000).toFixed(2)}`;
}

/* ── the checks on a review line's page ──
   Proctor's review checks and Reeve's watch-set are one roster with two
   voices; everything that differs between them is here. */
export const CHECKS_LOADING = "Reading the checks.";
export const CHECKS_ERROR = "Could not read the checks just now.";
export const CHECKS_RETRY = "Retry";
/** A core check: the review never runs without it. */
export const CHECKS_ALWAYS_ON = "always on";
/** The lead-in on a check the Team wrote, before what it looks for. */
export const CHECKS_YOURS = "your check";
export const CHECKS_INSTRUCTIONS_OPEN = "instructions";
export const CHECKS_INSTRUCTIONS_ADD = "add instructions";
export const CHECKS_INSTRUCTIONS_HIDE = "hide instructions";
export const CHECKS_RETIRE = "Retire";
export const CHECKS_RETIRE_CONFIRM = "Retire this check? It stops applying to the next review.";
export const CHECKS_ADD = "Add";
export const CHECKS_ADD_NAME = "Name";
export const CHECKS_ADD_ONLY_ON = "Only on";
export const CHECKS_ADD_ONLY_ON_PLACEHOLDER = "paths, optional, e.g. **/billing/**";
export const CHECKS_SAVE_FAILED = "Saving failed.";
export const CHECKS_NAME_UNSLUGGABLE = "Give the name a letter or number and it can save.";
export const CHECKS_SLUG_TAKEN = "That name is already on this roster · pick another.";

export interface ChecksCopy {
  /** The section's heading on the line's page. */
  title: string;
  /** The half-sentence beside the heading. */
  lead: string;
  newButton: string;
  whatLabel: string;
  namePlaceholder: string;
  instructionsPlaceholder: string;
  deltaPlaceholder: string;
  cancel: string;
}

export const CHECKS_COPY_CODE_REVIEW: ChecksCopy = {
  title: "Checks",
  lead: "lenses inside the one review, never a second reviewer",
  newButton: "Add a check",
  whatLabel: "What it looks for",
  namePlaceholder: "Accessibility",
  instructionsPlaceholder: "Written like a brief: what to flag, what to ignore, what always fails.",
  deltaPlaceholder: "What this Team wants this check to also watch for.",
  cancel: "Cancel",
};

export const CHECKS_COPY_PLAN_REVIEW: ChecksCopy = {
  title: "Concerns",
  lead: "what gets raised when a plan touches them",
  newButton: "Add a concern",
  whatLabel: "What to raise",
  namePlaceholder: "Data migrations",
  instructionsPlaceholder: "What to raise when a plan touches it.",
  deltaPlaceholder: "What this Team also wants raised, in your words.",
  cancel: "Cancel",
};

/** The voice for a workflow; an unknown one reads in the review voice. */
export function checksCopyFor(workflow: string): ChecksCopy {
  return workflow === "plan_review" ? CHECKS_COPY_PLAN_REVIEW : CHECKS_COPY_CODE_REVIEW;
}

// ── Team settings · the Team ladder ────────────────────────────────────────

export const AUTO_MERGE_CONDITIONS =
  "YAGNI merges a pull request this Team opened once CI is green, Proctor approved its latest push, and no person asked for changes or left a review thread open.";

/** The one Team-level control (decision 7, 2026-10-19): three steps, in order. */
export const LADDER_LABEL = "How far it goes on its own";
export const LADDER_STEPS: Record<TeamAutonomy, { label: string; person: string }> = {
  supervised: {
    label: "Supervised",
    person: "A person approves every plan and merges every pull request.",
  },
  autonomous: {
    label: "Autonomous",
    person:
      "A person only merges: nothing waits for a click before that, and Proctor approves on GitHub.",
  },
  autonomous_auto_merge: {
    label: "Autonomous + auto-merge",
    person: `A person only steps in to object: ${AUTO_MERGE_CONDITIONS}`,
  },
};
export const LADDER_CUSTOM = "Custom (override)";
export const LADDER_CUSTOM_NOTE =
  "Some Agents on this Team were set one by one, so it matches no step. Pick a step to set every Agent to it.";

// ── Team settings · Reeve's UI preview ─────────────────────────────────────

export const UI_PREVIEW_LABEL = "UI preview";
export const UI_PREVIEW_ON = "On";
export const UI_PREVIEW_OFF = "Off";
export const UI_PREVIEW_HELP_ON =
  "When a plan changes what a person sees, Reeve drafts a preview of today's screen and the change, and attaches it to the plan.";
export const UI_PREVIEW_HELP_OFF =
  "Off: plans get no preview and no notice. You can still create a prototype on the ticket.";

// ── Team settings · Connections (what the Team reads, YAG-986) ─────────────

export const CONNECTIONS_LABEL = "Connections";
export const CONNECTIONS_HELP =
  "Every workspace connection reaches this Team's reads unless it is hidden. Inside Jira, Confluence, Slack, Linear and Notion, only what is open to the workspace or granted to this Team is read.";
export const CONNECTIONS_NONE = "Nothing is connected in this workspace yet.";
export const CONNECTIONS_UNREAD = "Could not read what this Team reads.";

/** The customer's name for a connection ("Database"), never the id. */
export function connectionLabel(source: string): string {
  return getConnectorCatalogEntry(source)?.label ?? source;
}

/** The one line under the header: what the Team reads, in a sentence. */
export function readsSummaryLine(hidden: readonly string[]): string {
  if (hidden.length === 0) return "Reads every workspace connection.";
  return `Reads every workspace connection except ${listWords(hidden.map(connectionLabel))}.`;
}

/** The connector's own noun for what it holds; never say "container" to a person. */
const CONTAINER_NOUNS: Record<string, { singular: string; plural: string }> = {
  jira: { singular: "project", plural: "projects" },
  confluence: { singular: "space", plural: "spaces" },
  slack: { singular: "channel", plural: "channels" },
  linear: { singular: "team", plural: "teams" },
  notion: { singular: "space", plural: "spaces" },
  trello: { singular: "board", plural: "boards" },
  github: { singular: "repository", plural: "repositories" },
};

export function containerNoun(source: string, count: number): string {
  const nouns = CONTAINER_NOUNS[source] ?? { singular: "source", plural: "sources" };
  return count === 1 ? nouns.singular : nouns.plural;
}

/** The connection row's fact: "reads", "reads 3 of 7 projects", "hidden". */
export function connectionReadsFact(connection: {
  source: string;
  hidden: boolean;
  containers: ReadonlyArray<{ state: "open" | "team" | "dark" }> | null;
  /** Dark containers the viewer may not see the names of; counted, not listed. */
  darkUnnamed?: number;
}): string {
  if (connection.hidden) return "hidden";
  const named = connection.containers ?? [];
  const total = named.length + (connection.darkUnnamed ?? 0);
  if (total === 0) return "reads";
  const read = named.filter((c) => c.state !== "dark").length;
  return `reads ${read} of ${total} ${containerNoun(connection.source, total)}`;
}

/** The line for dark containers whose names the viewer may not see. */
export function darkUnnamedLine(source: string, count: number): string {
  const noun = containerNoun(source, count);
  return count === 1
    ? `1 ${noun} is dark. An admin can open it.`
    : `${count} ${noun} are dark. An admin can open them.`;
}

export const CONNECTIONS_RETRY = "Try again";

export const CONTAINER_STATE_WORD = { open: "open", team: "this Team", dark: "dark" } as const;

export const CONTAINER_ACTION_LABEL = {
  open_to_workspace: "Open to workspace",
  grant: "Grant to this Team",
  ask: "Ask an admin to open it.",
  none: "",
} as const;

/** Why a container is dark, when the connector says something about it. */
export function darkWhy(
  source: string,
  privacyClass: "public" | "private" | "shared" | "unknown",
): string | null {
  const name = connectionLabel(source);
  if (privacyClass === "unknown") return `${name} does not say whether it is private.`;
  if (privacyClass === "private") return `Private in ${name}.`;
  if (privacyClass === "shared") return `Shared with some people in ${name}.`;
  return null;
}

export const HIDE_CONNECTION = "Hide";
export const SHOW_CONNECTION = "Show";
export function hiddenConnectionLine(source: string): string {
  return `${connectionLabel(source)} is hidden from this Team: not read by default, and off the Team's own tools.`;
}

/** "a", "a and b", "a, b and c". */
function listWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`;
}
